import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check, CaretDown, Lock } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSiteImages } from "../context/SiteImagesContext";
import { toast } from "sonner";

export default function Colecciones() {
  const [collections, setCollections] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [filter, setFilter] = useState("todas");

  useEffect(() => {
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  const visible =
    filter === "todas" ? collections : collections.filter((c) => c.slug === filter);

  return (
    <section id="colecciones" data-testid="colecciones-section" className="py-24 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <Reveal>
            <p className="overline mb-6">
              <span className="text-[#D7C6A3]">05</span> &nbsp;/&nbsp; Colecciones
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D3732] leading-tight max-w-lg">
              Elige el diseño que conecta contigo.
            </h2>
            <p className="text-[#6B705C] mt-4 max-w-md font-light">
              Elige modelo, tamaño y acabado, añade especialidades y personaliza tu carpeta con tu
              nombre.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="flex flex-wrap gap-2">
            <FilterChip label="Todas" active={filter === "todas"} onClick={() => setFilter("todas")} />
            {collections.map((c) => (
              <FilterChip
                key={c.slug}
                label={c.name}
                active={filter === c.slug}
                onClick={() => setFilter(c.slug)}
              />
            ))}
          </Reveal>
        </div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((c) => (
              <CollectionCard key={c.slug} collection={c} pricing={pricing} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

function CollectionCard({ collection: c, pricing }) {
  const { addItem } = useCart();
  const { resolve } = useSiteImages();
  const [size, setSize] = useState("40");
  const [finish, setFinish] = useState("blanco");
  const [extras, setExtras] = useState([]);
  const [name, setName] = useState("");
  const [showSpecs, setShowSpecs] = useState(false);
  const [gift, setGift] = useState(false);
  const [giftMsg, setGiftMsg] = useState("");
  const [addPen, setAddPen] = useState(false);
  const [addMagnet, setAddMagnet] = useState(false);

  // If the current finish is not available for the new size, snap back to blanco (default).
  const finishAllowed = pricing?.finish_allowed_sizes || {};
  useEffect(() => {
    const allowed = finishAllowed[finish];
    if (allowed && !allowed.includes(size)) {
      setFinish("blanco");
    }
  }, [size, finish, finishAllowed]);

  const fixed = c.fixed_price;
  const base = pricing?.matrix?.[size]?.[finish];
  const extraFee = pricing?.separator_extra ?? 2;
  const giftFee = pricing?.gift_wrap?.price ?? 3;
  const giftMaxWords = pricing?.gift_wrap?.max_words ?? 300;
  // How many specialties are included FREE with the current size (customer picks freely).
  const includedBySize = pricing?.included_specialties_by_size || {"40": 4, "60": 6, "80": 8};
  const includedTotal = includedBySize[size] ?? 4;
  const freeExtras = includedTotal; // customer picks all specialties freely up to this budget
  const chargeableExtras = fixed ? extras.length : Math.max(0, extras.length - freeExtras);
  // For fixed-price models (e.g. Maternidad), extras exclude what the model already includes.
  // For standard models, the customer picks any specialty from the full índice.
  const allSpecs = pricing?.all_specialties || [];
  const modelChoosable = fixed
    ? allSpecs.filter((s) => !(c.includes || []).includes(s))
    : allSpecs;
  const basePrice =
    fixed != null
      ? fixed + chargeableExtras * extraFee
      : base != null
      ? base + chargeableExtras * extraFee
      : null;
  const price = basePrice != null ? basePrice + (gift ? giftFee : 0) : null;
  const penPrice = pricing?.pens?.unit_price;
  const magnetPrice = pricing?.magnets?.unit_price;
  const includes = c.includes || pricing?.base_separators || [];
  const choosable = modelChoosable;
  const unavailable = c.available === false;
  const giftWords = giftMsg.trim() ? giftMsg.trim().split(/\s+/).length : 0;
  const tooManyWords = giftWords > giftMaxWords;

  const toggleExtra = (nameSpec) => {
    setExtras((prev) =>
      prev.includes(nameSpec) ? prev.filter((s) => s !== nameSpec) : [...prev, nameSpec]
    );
  };

  const add = () => {
    if (price == null) return;
    if (unavailable) {
      toast("Este modelo estará disponible próximamente");
      return;
    }
    if (gift && tooManyWords) {
      toast.error(`La dedicatoria supera ${giftMaxWords} palabras`);
      return;
    }
    const sizeLabel = fixed ? "Edición especial" : pricing.sizes.find((s) => s.id === size)?.label;
    const finishLabel = fixed ? "" : pricing.finishes.find((f) => f.id === finish)?.label;
    const chosen = extras;
    const trimmedMsg = gift ? giftMsg.trim() : "";
    const key = `${c.id}__${size}__${finish}__${[...chosen].sort().join("-")}__${name.trim()}__${gift ? "g" : "n"}__${trimmedMsg.slice(0, 40)}`;
    addItem({
      key,
      model_id: c.id,
      model_name: c.name,
      size,
      size_label: sizeLabel,
      finish,
      finish_label: finishLabel,
      specialties: chosen,
      name_personalization: name.trim() || null,
      gift_wrap: gift,
      gift_message: trimmedMsg || null,
      price,
      image: resolve(`model:${c.slug}`, c.image),
      slug: c.slug,
    });
    // Optional add-ons: pen and/or solidarity magnet as separate cart lines.
    if (addPen) {
      const p = pricing?.pens;
      const rosa = p?.colors?.find((col) => col.id === "rosa") || { label: "Rosa pastel", hex: "#F6CFDA" };
      addItem({
        key: "pen__rosa__1",
        kind: "pen",
        pen_color: "rosa",
        pen_qty: 1,
        model_id: null,
        model_name: "Bolígrafo VITAE",
        size: null,
        size_label: `${rosa.label} · 1 unidad`,
        finish: null,
        finish_label: "",
        specialties: [],
        name_personalization: null,
        price: p?.unit_price ?? 3,
        image: resolve("pen:rosa", "") || null,
        pen_hex: rosa.hex,
        slug: "pen-rosa-1",
      });
      setAddPen(false);
    }
    if (addMagnet) {
      const m = pricing?.magnets;
      addItem({
        key: "magnet__vitae__1",
        kind: "magnet",
        magnet_design: "vitae",
        upload_ids: [],
        magnet_qty: 1,
        model_id: null,
        model_name: "Imán solidario VITAE",
        size: null,
        size_label: `Imán VITAE · ${m?.size || "5×5 cm"} · 1 unidad`,
        finish: null,
        finish_label: "",
        specialties: [],
        name_personalization: null,
        price: m?.unit_price ?? 3,
        image: resolve("iman:vitae", "") || null,
        pen_hex: "#EBD9D2",
        slug: "iman-vitae-1",
      });
      setAddMagnet(false);
    }
    toast.success(`${c.name} · ${sizeLabel} añadida al carrito`);
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`collection-${c.slug}`}
      className="group rounded-[2rem] overflow-hidden border border-[#EBEBEB] bg-[#FAF9F6] flex flex-col"
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={resolve(`model:${c.slug}`, c.image)}
          alt={c.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
        />
        <span className="absolute top-4 left-4 w-3 h-3 rounded-full" style={{ backgroundColor: c.accent }} />
        {c.badge && !unavailable && (
          <span className="absolute top-4 right-4 bg-[#A3B18A] text-white text-[0.6rem] tracking-wide uppercase rounded-full px-3 py-1">
            {c.badge}
          </span>
        )}
        {unavailable && (
          <span className="absolute top-4 right-4 bg-[#2D3732]/85 text-white text-[0.6rem] tracking-wide uppercase rounded-full px-3 py-1">
            Próximamente
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl text-[#2D3732]">{c.name}</h3>
        <p className="text-[#6B705C] text-sm mt-1 italic font-light">{c.tagline}</p>

        {/* Incluye siempre — para maternidad se muestran los separadores fijos;
            para el resto, se indica el nº de especialidades a elegir según tamaño. */}
        {fixed ? (
          <>
            <p className="overline text-[0.55rem] mt-5 mb-2">Incluye</p>
            <div className="flex flex-wrap gap-1.5">
              {includes.map((inc) => (
                <span
                  key={inc}
                  className="text-[0.65rem] rounded-full bg-[#EEF1E8] text-[#4A554E] border border-[#DDE3D2] px-2.5 py-1 flex items-center gap-1"
                >
                  <Lock size={9} weight="fill" className="text-[#8A9A6F]" />
                  {inc}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-xs text-[#4A554E] mt-5 leading-relaxed">
            Incluye <strong className="text-[#2D3732] font-normal">
              {includedTotal} especialidades a elegir
            </strong> del índice sin coste. Extras a {extraFee} € cada una.
          </p>
        )}

        {!fixed && (
        <>
        {/* Tamaño */}
        <p className="overline text-[0.55rem] mt-4 mb-2">Tamaño</p>
        <div className="flex gap-2">
          {(pricing?.sizes || []).map((s) => (
            <button
              key={s.id}
              data-testid={`size-${c.slug}-${s.id}`}
              onClick={() => setSize(s.id)}
              className={`flex-1 rounded-xl border py-2 text-xs transition-colors duration-300 ${
                size === s.id
                  ? "bg-[#2D3732] text-white border-[#2D3732]"
                  : "bg-white text-[#4A554E] border-[#EBEBEB] hover:border-[#A3B18A]"
              }`}
            >
              {s.fundas}
            </button>
          ))}
        </div>

        {/* Acabado */}
        <p className="overline text-[0.55rem] mt-4 mb-2">Acabado</p>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
          {(pricing?.finishes || []).map((f) => {
            const isAllowed = (finishAllowed[f.id] || []).includes(size);
            const isSelected = finish === f.id && isAllowed;
            return (
              <button
                key={f.id}
                data-testid={`finish-${c.slug}-${f.id}`}
                onClick={() => isAllowed && setFinish(f.id)}
                disabled={!isAllowed}
                title={
                  isAllowed
                    ? `${f.label}${f.surcharge > 0 ? " (+2€)" : ""}`
                    : `${f.label} — disponible solo en 40 fundas`
                }
                className={`relative rounded-lg border-2 aspect-square transition-all duration-200 ${
                  isSelected
                    ? "border-[#2D3732] scale-110"
                    : isAllowed
                    ? "border-[#EBEBEB] hover:border-[#A3B18A]"
                    : "border-[#EBEBEB] opacity-30 cursor-not-allowed"
                }`}
                style={{ backgroundColor: f.hex }}
              >
                {!isAllowed && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-full h-[1px] bg-[#B7BBB2] rotate-45" />
                  </span>
                )}
                {isSelected && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <Check size={14} weight="bold" className="text-white drop-shadow" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[0.65rem] text-[#4A554E] mt-1.5">
          {(pricing?.finishes || []).find((f) => f.id === finish)?.label}
          {(pricing?.finishes || []).find((f) => f.id === finish)?.surcharge > 0 && " (+2€)"}
        </p>
        {finish !== "blanco" && (
          <p className="text-[0.65rem] text-[#8A9A6F] mt-0.5">
            Los colores están disponibles solo en 40 fundas.
          </p>
        )}
        </>
        )}

        {/* Añadir especialidades — se muestra también para Maternidad (fixed_price) */}
        {choosable.length > 0 && (
          <>
            <button
              data-testid={`specs-toggle-${c.slug}`}
              onClick={() => setShowSpecs((v) => !v)}
              className="flex items-center justify-between mt-4 text-left"
            >
              <span className="overline text-[0.55rem]">
                Elegir especialidades
                {!fixed && (
                  <span className="text-[#8A9A6F]">
                    {" · "}
                    {Math.min(extras.length, freeExtras)}/{freeExtras} incluidas
                    {chargeableExtras > 0 && ` + ${chargeableExtras} × ${extraFee}€`}
                  </span>
                )}
                {fixed && extras.length > 0 && (
                  <span className="text-[#8A9A6F]"> · +{extras.length} × {extraFee}€</span>
                )}
              </span>
              <CaretDown
                size={14}
                className={`text-[#6B705C] transition-transform duration-300 ${showSpecs ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {showSpecs && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {choosable.map((nameSpec) => {
                      const on = extras.includes(nameSpec);
                      const positionIfSelected = on
                        ? extras.indexOf(nameSpec) + 1
                        : extras.length + 1;
                      const willBeFree = !fixed && positionIfSelected <= freeExtras;
                      return (
                        <button
                          key={nameSpec}
                          data-testid={`spec-${c.slug}-${nameSpec}`}
                          onClick={() => toggleExtra(nameSpec)}
                          className={`text-[0.65rem] rounded-full border px-2.5 py-1 transition-colors duration-200 flex items-center gap-1 ${
                            on
                              ? "bg-[#A3B18A] text-white border-[#A3B18A]"
                              : "bg-white text-[#4A554E] border-[#EBEBEB] hover:border-[#A3B18A]"
                          }`}
                        >
                          {on && <Check size={10} weight="bold" />}
                          {nameSpec}{" "}
                          <span className={on ? "opacity-90" : "opacity-60"}>
                            {willBeFree ? "(incluida)" : `(+${extraFee}€)`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Personalización del nombre */}
        <p className="overline text-[0.55rem] mt-4 mb-2">Personaliza con tu nombre</p>
        <input
          data-testid={`name-${c.slug}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          placeholder="Nombre para la carpeta (opcional)"
          className="w-full bg-white border border-[#EBEBEB] rounded-xl px-3 py-2 text-xs text-[#2D3732] placeholder:text-[#B7BBB2] focus:outline-none focus:ring-2 focus:ring-[#A3B18A]"
        />

        {/* Envolver para regalo con dedicatoria */}
        <label
          className="flex items-center gap-2 mt-4 text-xs text-[#4A554E] cursor-pointer select-none"
          data-testid={`gift-toggle-${c.slug}`}
        >
          <input
            type="checkbox"
            checked={gift}
            onChange={(e) => setGift(e.target.checked)}
            className="accent-[#A3B18A] w-4 h-4"
          />
          <span>
            Envolver para regalo con tarjeta dedicada{" "}
            <span className="text-[#8A9A6F]">(+{giftFee.toFixed(2)} €)</span>
          </span>
        </label>
        <AnimatePresence initial={false}>
          {gift && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <p className="overline text-[0.55rem] mt-3 mb-2">
                Dedicatoria (hasta {giftMaxWords} palabras)
              </p>
              <textarea
                data-testid={`gift-msg-${c.slug}`}
                value={giftMsg}
                onChange={(e) => setGiftMsg(e.target.value)}
                rows={4}
                placeholder="Escribe tu mensaje para acompañar el regalo…"
                className={`w-full bg-white border rounded-xl px-3 py-2 text-xs text-[#2D3732] placeholder:text-[#B7BBB2] focus:outline-none focus:ring-2 resize-y ${
                  tooManyWords
                    ? "border-[#c66] focus:ring-[#c66]"
                    : "border-[#EBEBEB] focus:ring-[#A3B18A]"
                }`}
              />
              <p
                className={`text-[0.65rem] mt-1 ${
                  tooManyWords ? "text-[#c66]" : "text-[#9CA3AF]"
                }`}
              >
                {giftWords}/{giftMaxWords} palabras
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Extras: bolígrafo o imán junto a la carpeta */}
        {(penPrice != null || magnetPrice != null) && (
          <div className="mt-4">
            <p className="overline text-[0.55rem] mb-2">Completa tu carpeta</p>
            {penPrice != null && (
              <label
                className="flex items-center gap-2 text-xs text-[#4A554E] cursor-pointer select-none mb-1.5"
                data-testid={`addon-pen-${c.slug}`}
              >
                <input
                  type="checkbox"
                  checked={addPen}
                  onChange={(e) => setAddPen(e.target.checked)}
                  className="accent-[#A3B18A] w-4 h-4"
                />
                <span>
                  Añadir bolígrafo VITAE rosa pastel{" "}
                  <span className="text-[#8A9A6F]">(+{penPrice.toFixed(2)} €)</span>
                </span>
              </label>
            )}
            {magnetPrice != null && (
              <label
                className="flex items-center gap-2 text-xs text-[#4A554E] cursor-pointer select-none"
                data-testid={`addon-magnet-${c.slug}`}
              >
                <input
                  type="checkbox"
                  checked={addMagnet}
                  onChange={(e) => setAddMagnet(e.target.checked)}
                  className="accent-[#A3B18A] w-4 h-4"
                />
                <span>
                  Añadir imán solidario{" "}
                  <span className="text-[#8A9A6F]">(+{magnetPrice.toFixed(2)} €)</span>
                </span>
              </label>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#EBEBEB]">
          <span data-testid={`price-${c.slug}`} className="font-display text-2xl text-[#2D3732]">
            {price != null ? `${price.toFixed(2)} €` : "—"}
          </span>
          <button
            data-testid={`add-${c.slug}`}
            onClick={add}
            disabled={unavailable}
            className="group/btn bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full pl-5 pr-4 py-3 text-sm flex items-center gap-2 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {unavailable ? "Próximamente" : "Añadir"}
            {!unavailable && (
              <Plus size={16} className="group-hover/btn:rotate-90 transition-transform duration-300" />
            )}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`filter-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`px-5 py-2 rounded-full text-sm border transition-colors duration-300 ${
        active
          ? "bg-[#2D3732] text-white border-[#2D3732]"
          : "bg-white text-[#4A554E] border-[#EBEBEB] hover:border-[#A3B18A]"
      }`}
    >
      {label}
    </button>
  );
}
